/**
 * The upsell shown beside the free tools on fly-social.com: the Fly Agent,
 * since 27 Oct 26 (Shardul: "switch them to agent"; it used to sell the
 * white-label tools to agencies). Every button opens the agent's landing page
 * with the tool and the slot on the address and no utm_* tags
 * (agentPromoConfig.ts flySiteAgentHref), and fires the GA4 event
 * fly_agent_promo_click.
 *
 * Three surfaces, one component:
 *
 *   side rails   fixed panels in the empty margins either side of the tool.
 *                The tool column is max-w-6xl, so on a wide screen there is
 *                real dead space there and nothing has to move to make room.
 *   inline card  the same pitch, in the flow, for screens too narrow for rails
 *   top banner   a slim "free to try" strip once a report exists
 *
 * NOTHING RENDERS unless shouldShowFreeToolAds passes. See freeToolAdConfig.ts for
 * why that is an allowlist: these tools also run as white-label embeds inside
 * agencies' own websites, and advertising Fly there would pitch our product to
 * our customer's clients, on their site.
 *
 * WHY THE RAIL WIDTH IS A LADDER, NOT ONE NUMBER
 *
 * The free margin either side is (viewport - 1152) / 2, so it grows fast: 274px
 * at 1700, 384px at 1920, 704px at 2560. A single fixed width has to be sized
 * for the narrowest case, which left a 216px card marooned in 384px of space on
 * a normal 1920 monitor. The widths below step up so the card keeps filling the
 * gap instead of floating in it, each one still leaving a real gutter:
 *
 *   from 1700px   256px rail in a 274px margin
 *   from 1920px   344px rail in a 384px margin
 *   from 2200px   400px rail in a 524px margin
 *
 * These are the RAIL widths including their own padding, which is what the
 * measurement compares against the margin. A first pass sized the card and
 * forgot the padding, so every rail came out 40px narrower than intended and
 * still looked marooned.
 *
 * WHY THE BREAKPOINT IS 1700px AND NOT AN OFF-THE-SHELF ONE
 *
 * The tool column is max-w-6xl, which is 1152px, so the free margin either side
 * is (viewport - 1152) / 2. A 216px rail plus its 16px page padding needs 232px,
 * call it 256px with a gap that does not look accidental. That means the rails
 * only fit from about 1664px upwards.
 *
 * This first shipped on Tailwind's `xl` (1280px), which was simply wrong, and
 * measuring proved it: at 1280, 1440 AND 1600 the rails sat ON TOP of the tool.
 * Those are ordinary laptop widths, so most visitors would have had an advert
 * covering the thing they came to use. Rounded up to 1700px, which clears the
 * content at every width with room to spare.
 *
 * Below that the inline card carries the message instead, in the flow, where it
 * cannot cover anything.
 */

import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowRight, CalendarDays, Sparkles, X } from 'lucide-react';
import { useEmbed } from '@/contexts/EmbedContext';
import { trackGoogleEvent } from '@/utils/tracking';
import { flySiteAgentHref } from './agent-promo/agentPromoConfig';
import { useToolAdStage } from './ToolAdStageContext';
import { useWaitVideoActive } from '../wait-video/WaitVideoContext';
import { AD_COPY, BOOK_DEMO_URL, shouldShowFreeToolAds, toolSlugFromPath, type AdCopy } from './freeToolAdConfig';

/** One GA4 event per click on the agent's button, never a failed click. */
function trackAgentClick(toolSlug: string | null, placement: string): void {
  try {
    trackGoogleEvent('fly_agent_promo_click', { tool_slug: toolSlug, placement });
  } catch {
    /* analytics only */
  }
}

/** Remembers a dismissal for the session only, so it does not nag on the next click. */
const DISMISS_KEY = 'fly.freeToolAds.dismissed';

function useDismissed(): [boolean, () => void] {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      // Private windows and blocked site data throw on access. Treat that as
      // "not dismissed" rather than letting it break the page.
      return false;
    }
  });
  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* nothing to do, it just will not persist */
    }
  };
  return [dismissed, dismiss];
}

function AdCard({ copy, onDismiss, compact, slot, toolSlug }: { copy: AdCopy; onDismiss?: () => void; compact?: boolean; slot: string; toolSlug: string | null }) {
  return (
    <div
      data-fly-ad={slot}
      className={`relative rounded-2xl border border-violet-400/25 bg-gradient-to-b from-violet-950/80 to-slate-950/90 backdrop-blur-sm shadow-xl shadow-violet-950/40 ${
        compact ? 'p-4' : 'p-5'
      }`}
    >
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Hide this offer"
          className="absolute right-2 top-2 rounded-md p-1 text-slate-500 transition hover:bg-white/5 hover:text-slate-300"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}

      <div className="flex items-center gap-1.5 whitespace-nowrap text-[10.5px] font-semibold uppercase tracking-[0.08em] text-violet-300">
        <Sparkles className="h-3 w-3" />
        {copy.eyebrow}
      </div>

      <h3 className={`mt-2 font-semibold leading-snug text-white ${compact ? 'text-[15px]' : 'text-base'}`}>
        {copy.headline}
      </h3>

      <p className="mt-2 text-[13px] leading-relaxed text-slate-300">{copy.body}</p>

      {/* A plain <a>: the landing page is a static page served ahead of the app, so a full load is the right navigation. */}
      <a
        href={flySiteAgentHref(toolSlug, slot)}
        onClick={() => trackAgentClick(toolSlug, slot)}
        className="mt-4 inline-flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-violet-600 px-3 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-500"
      >
        {copy.cta}
        <ArrowRight className="h-3.5 w-3.5" />
      </a>

      {/*
        Secondary action. Someone not ready to start a trial will often still
        take a conversation, so both doors are open: the trial for people who
        want to self-serve, a demo for people who want to be shown.

        A plain <a> and not react-router's <Link>: the calendar is an external
        site, so a client-side route push would just fail. target=_blank keeps
        the visitor's half-finished audit alive behind it, and rel=noopener is
        required with it.
      */}
      {copy.demo && (
        <a
          href={BOOK_DEMO_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-violet-400/30 px-3 py-2 text-[13px] font-medium text-violet-200 transition hover:border-violet-400/60 hover:bg-violet-500/10 hover:text-white"
        >
          <CalendarDays className="h-3.5 w-3.5" />
          {copy.demo}
        </a>
      )}

      <p className="mt-2 text-center text-[11px] text-slate-500">Free for 14 days. No card required.</p>
    </div>
  );
}

export function FreeToolAds() {
  const { pathname } = useLocation();
  const embed = useEmbed();
  const stage = useToolAdStage();
  const [dismissed, dismiss] = useDismissed();
  const videoOn = useWaitVideoActive();

  const eligible = shouldShowFreeToolAds(pathname, embed.isEmbed);
  const toolSlug = toolSlugFromPath(pathname);

  // Fade in rather than appear, so the panels do not snap into the corner of
  // the eye the instant scan is pressed.
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!eligible || dismissed || stage === 'idle') {
      setShown(false);
      return;
    }
    const t = setTimeout(() => setShown(true), 400);
    return () => clearTimeout(t);
  }, [eligible, dismissed, stage]);

  const cards = useMemo<AdCopy[]>(() => (stage === 'idle' ? [] : AD_COPY[stage] ?? []), [stage]);

  if (!eligible || dismissed || videoOn || stage === 'idle' || cards.length === 0) return null;

  return (
    <>
      {/*
        Side rails. Fixed, so they never push the tool around, and pointer
        events are disabled on the wrapper so the dead margin stays clickable
        through to the page. Only the cards themselves take clicks.
        xl and up only: see the note at the top of this file.
      */}
      <div
        aria-hidden={!shown}
        className={`pointer-events-none fixed inset-y-0 left-0 z-30 hidden w-[256px] min-[1920px]:w-[344px] min-[2200px]:w-[400px] items-end justify-center px-5 pb-10 transition-opacity duration-500 min-[1700px]:flex ${
          shown ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className="pointer-events-auto w-full">
          <AdCard copy={cards[0]} onDismiss={dismiss} slot="rail-left" toolSlug={toolSlug} />
        </div>
      </div>

      <div
        aria-hidden={!shown}
        className={`pointer-events-none fixed inset-y-0 right-0 z-30 hidden w-[256px] min-[1920px]:w-[344px] min-[2200px]:w-[400px] items-end justify-center px-5 pb-10 transition-opacity duration-500 min-[1700px]:flex ${
          shown ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className="pointer-events-auto w-full">
          {/* Falls back to the first card when a stage only defines one, so a
              rail is never left empty. */}
          <AdCard copy={cards[1] ?? cards[0]} onDismiss={dismiss} slot="rail-right" toolSlug={toolSlug} />
        </div>
      </div>

      {/*
        EVERY screen below the rail breakpoint, not just phones.

        This started as phones only, with tablets and laptops getting a card in
        the normal flow instead. Measuring on the machine it was reported from
        killed that idea: on a MacBook Air the card landed 3,880px down a
        4,176px page. It rendered, it passed every test, and no human would ever
        have scrolled far enough to see it.

        Between 768px and 1700px there is no margin to float in and the page is
        several screens tall, so the only place an advert can be both visible
        and out of the way is docked to the bottom. Deliberately a strip rather
        than a panel, so it never covers the report behind it.

        Dismissible, and the close control is a real 32px target rather than a
        6px cross, because an advert you cannot get rid of is worse than no
        advert. Dismissal is remembered for the session.
      */}
      <div
        className={`fixed inset-x-0 bottom-0 z-40 px-3 pb-3 transition-transform duration-500 min-[1700px]:hidden ${
          shown ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        {/* Centred and capped rather than edge to edge.
            Spanning the full viewport put the bar underneath whatever the page
            already has pinned to the bottom corners: on a wide screen the chat
            launcher sat on top of the "Try it free" button, and the strip ran
            under the widgets on both sides.

            max-w-5xl is the narrowest width that still fits the headline, the
            supporting line and both buttons on one row without wrapping. On a
            1280px screen that leaves ~128px clear each side, which is more than
            the launcher needs; below 1024px the cap stops binding and the bar
            goes back to full width minus its padding, which is correct on a
            phone where there is nothing beside it anyway. */}
        <div
          data-fly-ad="mobile"
          className="relative mx-auto flex w-full max-w-5xl items-center gap-3 rounded-2xl border border-violet-400/30 bg-gradient-to-r from-violet-950/95 to-slate-950/95 p-3 pr-11 shadow-2xl shadow-black/50 backdrop-blur"
        >
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-semibold leading-tight text-white sm:text-sm">{cards[0].headline}</p>
            {/* The supporting line only appears where there is room for it. On a
                narrow phone it would push the bar to two lines. */}
            <p className="mt-0.5 hidden truncate text-xs leading-tight text-slate-400 sm:block">{cards[0].body}</p>
            <p className="mt-0.5 text-[11px] leading-tight text-slate-400 sm:hidden">Free for 14 days. No card required.</p>
          </div>
          {cards[0].demo && (
            <a
              href={BOOK_DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-violet-400/30 px-3 py-2 text-[13px] font-medium text-violet-200 transition hover:border-violet-400/60 hover:bg-violet-500/10 hover:text-white md:inline-flex"
            >
              <CalendarDays className="h-3.5 w-3.5" />
              {cards[0].demo}
            </a>
          )}
          <a
            href={flySiteAgentHref(toolSlug, 'strip')}
            onClick={() => trackAgentClick(toolSlug, 'strip')}
            className="shrink-0 whitespace-nowrap rounded-lg bg-violet-600 px-3.5 py-2 text-[13px] font-semibold text-white transition hover:bg-violet-500"
          >
            {cards[0].cta}
          </a>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Close this offer"
            className="absolute right-1 top-1 rounded-lg p-2 text-slate-400 transition hover:bg-white/10 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  );
}

/**
 * The slim "try it free" strip. Separate from the rails because it belongs at
 * the top of the page, above the report, and appears only once there is a
 * report worth reacting to.
 */
export function FreeToolAdBanner() {
  const { pathname } = useLocation();
  const embed = useEmbed();
  const stage = useToolAdStage();
  const [dismissed, dismiss] = useDismissed();

  if (!shouldShowFreeToolAds(pathname, embed.isEmbed)) return null;
  if (dismissed || (stage !== 'ready' && stage !== 'unlocked')) return null;

  return (
    <div data-fly-ad="banner" className="mx-auto mb-6 w-full max-w-6xl px-4">
      <div className="flex flex-col items-start gap-3 rounded-xl border border-violet-400/25 bg-violet-950/50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-200">
          <span className="font-semibold text-white">Let Fly Agent fix what this report found.</span>{' '}
          Reviews, posts and every detail on your Google profile, minded every day.
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <a
            href={BOOK_DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-violet-400/30 px-3 py-2 text-sm font-medium text-violet-200 transition hover:border-violet-400/60 hover:bg-violet-500/10 hover:text-white"
          >
            <CalendarDays className="h-3.5 w-3.5" />
            Book a demo
          </a>
          <a
            href={flySiteAgentHref(toolSlugFromPath(pathname), 'top-banner')}
            onClick={() => trackAgentClick(toolSlugFromPath(pathname), 'top-banner')}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-violet-600 px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-violet-500"
          >
            Try it free
            <ArrowRight className="h-3.5 w-3.5" />
          </a>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Hide this offer"
            className="rounded-md p-1.5 text-slate-500 transition hover:bg-white/5 hover:text-slate-300"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
